import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextString from '../../components/CLocalizedTextString';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CModal from '../../components/CModal';
import CPageModule from '../../components/CPageModule';
import LinkTabButton from '../../components/LinkTabButton';
import LinkTabButtonWrapper from '../../components/LinkTabButtonWrapper';
import NotFoundModule from '../../components/PageGates/NotFoundModule';
import PageOutlet from '../../components/layout/PageOutlet';
import { factSheetSections } from '../../constants/factSheetSections';
import { EAppType } from '../../enums/AppType';
import { EFactsheetClasses } from '../../enums/EFactsheetClasses';
import { SourceType } from '../../enums/SourceType';
import { PageRoutes } from '../../enums/enums';
import {
  areTermsAcceptedAndValid,
  getFactsheetId,
  saveTermsAgreement,
  updateStateAttribute
} from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useUsageTrackingInfo } from '../../hooks/useUsageTrackingInfo';
import { type IRequestState } from '../../models/IRequestState';
import { type ISourceTuple } from '../../models/usage-tracking/ISourceTuple';
import type { IUsageTrackingInfo } from '../../models/usage-tracking/IUsageTracking';
import { SvUsageTrackingServices } from '../../services/SvUsageTrackingServices';
import PublicFactsheetServices from '../../services/publicFactsheetServices';
import {
  addBreadcrumbTextReplacement,
  purgeBreadcrumbTextReplacementDictForOutlets,
  updateFactsheetId
} from '../../store/appSlice';
import FactsheetStickyHeader from './FactsheetStickyHeader';
import PFactsheetTerms from './PFactsheetTerms';
import CFactsheetDisclaimer from '../../components/CFactsheetDisclaimer';

interface State {
  linkIsValid?: boolean;
  termsShown: boolean;
  reqState: IRequestState;
  targetScrollProgress?: number;
}

export default function PFactsheetOutlet() {
  const dispatch = useAppDispatch();
  const { factsheetId } = useParams();
  const keycloak = useKeycloak();
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboardOutletDimensions } = useAppSelector((s) => s.dashboard);

  // Initialize state with terms checking logic
  const [state, setState] = useState<State>({
    termsShown: false, // Will be set in useEffect based on localStorage check
    reqState: {
      isError: false,
      isLoading: false
    }
  });
  const targetRef = useRef<HTMLDivElement>(null);
  const localFactsheetId = getFactsheetId(EFactsheetClasses.US_AMERICAN_STOCKS);
  const factsheetClassLabel = CLocalizedTextString('factsheet.class.nexdosUsBuyoutStyleFund');
  const factsheetLabel = CLocalizedTextStringStatic('globalSectionFactsheet');

  const tuple: ISourceTuple = {
    sourceType: SourceType.Factsheet,
    sourceIdentifier: localFactsheetId as string
  };

  const usageTrackingParams: IUsageTrackingInfo = useUsageTrackingInfo(EAppType.FactsheetUsAmericanStocks, tuple);

  const doUserAcceptsTerms = (redirect: boolean) => {
    if (redirect) {
      navigate('../..');
      return;
    }

    // Save terms agreement to localStorage with expiration
    if (factsheetId) {
      saveTermsAgreement(EFactsheetClasses.US_AMERICAN_STOCKS, factsheetId);
    }

    updateStateAttribute<State>({ termsShown: false }, setState);
    dispatch(updateFactsheetId({ factsheetId }));
  };

  useEffect(() => {
    if (!localFactsheetId || !factsheetId) return;
    if (localFactsheetId !== factsheetId) return;
    (async () => {
      await SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams);
    })();
  }, [localFactsheetId, factsheetId]);

  useEffect(() => {
    // Check terms of service on component mount
    const checkTermsOfService = () => {
      if (factsheetId !== localFactsheetId) {
        updateStateAttribute<State>({ termsShown: true }, setState);
        return;
      }

      const termsValid = areTermsAcceptedAndValid(EFactsheetClasses.US_AMERICAN_STOCKS);

      // Show terms modal if not accepted or expired
      updateStateAttribute<State>({ termsShown: !termsValid }, setState);
      if (termsValid) {
        dispatch(updateFactsheetId({ factsheetId }));
      }
    };

    updateStateAttribute<State>({ reqState: { isLoading: true, isError: false } }, setState);
    // Validate factsheet and set terms visibility
    (async () => {
      const res = await PublicFactsheetServices.getFactsheetCounter(
        factsheetId ?? '',
        keycloak.keycloak.authenticated ? 'user' : 'public'
      );
      updateStateAttribute<State>(
        { linkIsValid: !res.hasError(), reqState: { isLoading: false, isError: res.hasError() } },
        setState
      );

      if (!res.hasError()) {
        // Check terms of service only if factsheet is valid
        checkTermsOfService();

        dispatch(
          addBreadcrumbTextReplacement([factsheetId, factsheetClassLabel]) // Only set the label in breadcrumb on success
        );
      }
    })();
    return () => {
      dispatch(purgeBreadcrumbTextReplacementDictForOutlets());
    };
  }, [factsheetId, keycloak.keycloak.authenticated, dispatch]);

  useEffect(() => {
    const progress: number = targetRef.current?.getBoundingClientRect().top ?? 0;

    if ((progress ?? 0) > 0) {
      updateStateAttribute<State>({ targetScrollProgress: progress }, setState);
    }
  }, [dashboardOutletDimensions.w, location.pathname, state.linkIsValid, state.termsShown]);

  const activeSectionIndex = factSheetSections.findIndex((section) =>
    section.to === PageRoutes.PUBLIC_FACTSHEET_INDEX
      ? location.pathname.replace(/\/$/, '').endsWith(`/${factsheetId}`)
      : location.pathname.endsWith(`/${section.to}`)
  );

  return (
    <>
      {state.reqState.isLoading && <CPageModule apiRequestState={state.reqState} />}

      {state.linkIsValid === false && <NotFoundModule />}

      {state.linkIsValid === true && (
        <>
          <CModal
            onClose={() => {
              doUserAcceptsTerms(true);
            }}
            open={state.termsShown}
          >
            <PFactsheetTerms
              onAccept={() => {
                doUserAcceptsTerms(false);
              }}
            />
          </CModal>

          {!state.termsShown && (
            <>
              <PageOutlet
                outletKey={PageRoutes.PUBLIC_FACTSHEET_COLLECTION_OUTLET}
                title={factsheetLabel}
                stickyHeaderConfig={
                  state.targetScrollProgress
                    ? {
                      content: <FactsheetStickyHeader />,
                      targetScrollProgress: state.targetScrollProgress,
                      pad: true
                    }
                    : undefined
                }
              >
                <CPageModule fullWidth>
                  <CPageModule submodule>
                    <CLocalizedText dictKey={'fsovFirm'} />
                  </CPageModule>

                  <div ref={targetRef}>
                    <LinkTabButtonWrapper id={'factsheet-sections'} activeIndex={activeSectionIndex}>
                      {factSheetSections.map((section) => (
                        <LinkTabButton
                          key={section.key}
                          to={{ pathname: section.to }}
                          label={section.dictKey}
                          text={<CLocalizedText dictKey={section.dictKey} />}
                          isSelected={
                            section.to === PageRoutes.PUBLIC_FACTSHEET_INDEX
                              ? location.pathname.replace(/\/$/, '').endsWith(`/${factsheetId}`)
                              : location.pathname.endsWith(`/${section.to}`)
                          }
                        />
                      ))}
                    </LinkTabButtonWrapper>
                  </div>
                </CPageModule>
              </PageOutlet>

              <CFactsheetDisclaimer />
            </>
          )}
        </>
      )}
    </>
  );
}
